import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { sendEmailGiftCards } from '@/app/[locale]/(default-layout)/(forms)/_components/send-email';

import Button from '@/components/button/Button';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconSuccessful16 } from '@cineamo/ui/src/icons/ic-successful';

import { track } from '@/utils/server-side-tracking';

import FormInput from './FormInput';
import FormTextarea from './FormTextarea';
import TurnstileWidget from './TurnstileWidget';

export type BuyGiftCardsFormData = {
    // Gift card quantities
    giftVoucher: number;
    concessionVoucher: number;
    dboxVoucher: number;
    goldenEnvelope: number;
    birthdayPackageClassic: number;
    birthdayPackagePremium: number;

    // Personal information
    firstName: string;
    lastName: string;
    firm?: string;
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    email: string;
    message?: string;
};

type BuyGiftCardsFormProps = {
    domainConfiguration: DomainConfigurationDto;
};

export default function BuyGiftCardsForm({ domainConfiguration }: BuyGiftCardsFormProps) {
    const t = useTranslations();
    const format = useFormat();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    // Get configured prices (stored in cents)
    const prices =
        domainConfiguration.cinema?._embedded?.cinemaConfiguration?.websiteConfiguration?.forms?.giftCards?.prices;

    // Helper to convert cents to euros and format as currency
    const formatPrice = (cents?: number) => {
        if (!cents) return '';
        const euros = cents / 100;
        return format.number.formatCurrency(euros, domainConfiguration.cinema?.currencyCode);
    };

    const { register, handleSubmit, watch, reset } = useForm<BuyGiftCardsFormData>({
        defaultValues: {
            giftVoucher: 0,
            concessionVoucher: 0,
            dboxVoucher: 0,
            goldenEnvelope: 0,
            birthdayPackageClassic: 0,
            birthdayPackagePremium: 0,
            firstName: '',
            lastName: '',
            firm: '',
            street: '',
            houseNumber: '',
            postalCode: '',
            city: '',
            email: '',
            message: ''
        }
    });

    const watchedValues = watch([
        'giftVoucher',
        'concessionVoucher',
        'dboxVoucher',
        'birthdayPackageClassic',
        'birthdayPackagePremium',
        'goldenEnvelope'
    ]);
    const totalItems = watchedValues.reduce((sum, value) => sum + (value || 0), 0);

    const onSubmit = async (data: BuyGiftCardsFormData) => {
        if (totalItems === 0) {
            showToast({
                toastType: toast.error,
                messageTitle: t('buy-gift-cards.error.title'),
                messageBody: t('buy-gift-cards.error.no-items')
            });
            return;
        }

        setIsProcessing(true);

        try {
            const success = await sendEmailGiftCards(data, turnstileToken ?? undefined);

            if (success) {
                setIsSuccess(true);
                reset();
                showToast({
                    toastType: toast.success,
                    messageIcon: <IconSuccessful16 />,
                    messageTitle: t('buy-gift-cards.success.title'),
                    messageBody: t('buy-gift-cards.success.message'),
                    backgroundColor: domainConfiguration.style.primaryColor,
                    textColor: domainConfiguration.style.onPrimaryColor
                });
            } else {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('buy-gift-cards.error.title'),
                    messageBody: t('buy-gift-cards.error.message')
                });
            }

            track('Buy Gift Cards Form Submitted', {
                cinema: domainConfiguration.cinema?.name,
                domain: domainConfiguration.domain,
                cinemaId: domainConfiguration.cinema?.id,
                totalItems: totalItems,
                data: JSON.stringify(data, null, 2),
                success: success
            });
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('buy-gift-cards.error.title'),
                messageBody: t('buy-gift-cards.error.network-message')
            });
        } finally {
            setIsProcessing(false);
        }
    };

    if (isSuccess) {
        return (
            <div
                className="backdrop-blur-sm rounded-12 p-12 shadow-lg text-center"
                style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                <IconSuccessful16
                    className="size-[48px] mx-auto mb-4"
                    style={{ color: domainConfiguration.style.primaryColor }}
                />
                <h3
                    className="text-headline-24 mb-2"
                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                    {t('buy-gift-cards.success.title')}
                </h3>
                <p
                    className="text-body-16"
                    style={{ color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8) }}>
                    {t('buy-gift-cards.success.message')}
                </p>
            </div>
        );
    }

    return (
        <div
            className="backdrop-blur-sm rounded-12 p-12 shadow-lg"
            style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
            <form
                className="space-y-24"
                onSubmit={handleSubmit(onSubmit)}>
                {/* Gift Card Selection */}
                <div className="space-y-6">
                    <h3
                        className="text-headline-20 font-semibold"
                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                        {t('buy-gift-cards.form.select-cards')}
                    </h3>

                    <div className="grid gap-6">
                        {/* Regular Gift Vouchers */}
                        {prices?.giftVoucher && (
                            <div
                                className="flex items-center justify-between p-6 rounded-8 border"
                                style={{
                                    borderColor: withOpacity(domainConfiguration.style.primaryColor, 0.2),
                                    backgroundColor: withOpacity(domainConfiguration.style.primaryColor, 0.05)
                                }}>
                                <div className="flex-1">
                                    <h4
                                        className="text-body-16 font-semibold"
                                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                        {t('buy-gift-cards.form.gift-voucher-label', {
                                            price: formatPrice(prices.giftVoucher)
                                        })}
                                    </h4>
                                    <p
                                        className="text-body-14"
                                        style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7) }}>
                                        {t('buy-gift-cards.form.gift-voucher-description')}
                                    </p>
                                </div>
                                <div className="w-64 ml-4">
                                    <FormInput
                                        name="giftVoucher"
                                        label="Anzahl"
                                        type="number"
                                        min={0}
                                        register={register}
                                        registerOptions={{ valueAsNumber: true, min: 0 }}
                                        domainConfiguration={domainConfiguration}
                                        showLabel={false}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Concession Vouchers */}
                        {prices?.concessionVoucher && (
                            <div
                                className="flex items-center justify-between p-6 rounded-8 border"
                                style={{
                                    borderColor: withOpacity(domainConfiguration.style.primaryColor, 0.2),
                                    backgroundColor: withOpacity(domainConfiguration.style.primaryColor, 0.05)
                                }}>
                                <div className="flex-1">
                                    <h4
                                        className="text-body-16 font-semibold"
                                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                        {t('buy-gift-cards.form.concession-voucher-label', {
                                            price: formatPrice(prices.concessionVoucher)
                                        })}
                                    </h4>
                                    <p
                                        className="text-body-14"
                                        style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7) }}>
                                        {t('buy-gift-cards.form.concession-voucher-description')}
                                    </p>
                                </div>
                                <div className="w-64 ml-4">
                                    <FormInput
                                        name="concessionVoucher"
                                        label="Anzahl"
                                        type="number"
                                        min={0}
                                        register={register}
                                        registerOptions={{ valueAsNumber: true, min: 0 }}
                                        domainConfiguration={domainConfiguration}
                                        showLabel={false}
                                    />
                                </div>
                            </div>
                        )}

                        {/* D-Box Vouchers */}
                        {prices?.dboxVoucher && (
                            <div
                                className="flex items-center justify-between p-6 rounded-8 border"
                                style={{
                                    borderColor: withOpacity(domainConfiguration.style.primaryColor, 0.2),
                                    backgroundColor: withOpacity(domainConfiguration.style.primaryColor, 0.05)
                                }}>
                                <div className="flex-1">
                                    <h4
                                        className="text-body-16 font-semibold"
                                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                        {t('buy-gift-cards.form.dbox-voucher-label', {
                                            price: formatPrice(prices.dboxVoucher)
                                        })}
                                    </h4>
                                    <p
                                        className="text-body-14"
                                        style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7) }}>
                                        {t('buy-gift-cards.form.dbox-voucher-description')}
                                    </p>
                                </div>
                                <div className="w-64 ml-4">
                                    <FormInput
                                        name="dboxVoucher"
                                        label="Anzahl"
                                        type="number"
                                        min={0}
                                        register={register}
                                        registerOptions={{ valueAsNumber: true, min: 0 }}
                                        domainConfiguration={domainConfiguration}
                                        showLabel={false}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Birthday Package Classic */}
                        {prices?.giftVoucher && prices?.concessionVoucher && (
                            <div
                                className="flex items-center justify-between p-6 rounded-8 border"
                                style={{
                                    borderColor: withOpacity(domainConfiguration.style.primaryColor, 0.2),
                                    backgroundColor: withOpacity(domainConfiguration.style.primaryColor, 0.05)
                                }}>
                                <div className="flex-1">
                                    <h4
                                        className="text-body-16 font-semibold"
                                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                        Gutschein-Paket Classic -{' '}
                                        {formatPrice(prices.giftVoucher * 3 + prices.concessionVoucher * 3)}
                                    </h4>
                                    <div
                                        className="text-body-14 mt-2 space-y-1"
                                        style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7) }}>
                                        <div>
                                            3 × {t('buy-gift-cards.types.regular')} à {formatPrice(prices.giftVoucher)}{' '}
                                            = {formatPrice(prices.giftVoucher * 3)}
                                        </div>
                                        <div>
                                            3 × Kioskgutscheine à {formatPrice(prices.concessionVoucher)} ={' '}
                                            {formatPrice(prices.concessionVoucher * 3)}
                                        </div>
                                    </div>
                                </div>
                                <div className="w-64 ml-4">
                                    <FormInput
                                        name="birthdayPackageClassic"
                                        label="Anzahl"
                                        type="number"
                                        min={0}
                                        register={register}
                                        registerOptions={{ valueAsNumber: true, min: 0 }}
                                        domainConfiguration={domainConfiguration}
                                        showLabel={false}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Birthday Package Premium */}
                        {prices?.giftVoucher && prices?.concessionVoucher && prices?.dboxVoucher && (
                            <div
                                className="flex items-center justify-between p-6 rounded-8 border"
                                style={{
                                    borderColor: withOpacity(domainConfiguration.style.primaryColor, 0.2),
                                    backgroundColor: withOpacity(domainConfiguration.style.primaryColor, 0.05)
                                }}>
                                <div className="flex-1">
                                    <h4
                                        className="text-body-16 font-semibold"
                                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                        Gutschein-Paket Premium -{' '}
                                        {formatPrice(
                                            prices.dboxVoucher * 2 +
                                                prices.giftVoucher * 2 +
                                                prices.concessionVoucher * 4
                                        )}
                                    </h4>
                                    <div
                                        className="text-body-14 mt-2 space-y-1"
                                        style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7) }}>
                                        <div>
                                            2 × D-Box Gutscheine à {formatPrice(prices.dboxVoucher)} ={' '}
                                            {formatPrice(prices.dboxVoucher * 2)}
                                        </div>
                                        <div>
                                            2 × {t('buy-gift-cards.types.regular')} à {formatPrice(prices.giftVoucher)}{' '}
                                            = {formatPrice(prices.giftVoucher * 2)}
                                        </div>
                                        <div>
                                            4 × Kioskgutscheine à {formatPrice(prices.concessionVoucher)} ={' '}
                                            {formatPrice(prices.concessionVoucher * 4)}
                                        </div>
                                    </div>
                                </div>
                                <div className="w-64 ml-4">
                                    <FormInput
                                        name="birthdayPackagePremium"
                                        label="Anzahl"
                                        type="number"
                                        min={0}
                                        register={register}
                                        registerOptions={{ valueAsNumber: true, min: 0 }}
                                        domainConfiguration={domainConfiguration}
                                        showLabel={false}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Golden Envelope */}
                        {prices?.goldenEnvelope && (
                            <div
                                className="flex items-center justify-between p-6 rounded-8 border"
                                style={{
                                    borderColor: withOpacity(domainConfiguration.style.primaryColor, 0.2),
                                    backgroundColor: withOpacity(domainConfiguration.style.primaryColor, 0.05)
                                }}>
                                <div className="flex-1">
                                    <h4
                                        className="text-body-16 font-semibold"
                                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                        Goldener Umschlag - {formatPrice(prices.goldenEnvelope)}
                                    </h4>
                                    <p
                                        className="text-body-14"
                                        style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7) }}>
                                        Luxuriöser goldener Umschlag für Ihre Geschenkgutscheine
                                    </p>
                                </div>
                                <div className="w-64 ml-4">
                                    <FormInput
                                        name="goldenEnvelope"
                                        label="Anzahl"
                                        type="number"
                                        min={0}
                                        max={1}
                                        register={register}
                                        registerOptions={{ valueAsNumber: true, min: 0, max: 1 }}
                                        domainConfiguration={domainConfiguration}
                                        showLabel={false}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Processing Fee Info */}
                    {prices?.processingFee && (
                        <div
                            className="p-24 rounded-8 text-center border-2 border-dashed"
                            style={{
                                backgroundColor: withOpacity(domainConfiguration.style.onSurfaceColor, 0.05),
                                borderColor: withOpacity(domainConfiguration.style.onSurfaceColor, 0.2)
                            }}>
                            <p
                                className="text-body-14"
                                style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.8) }}>
                                {t('buy-gift-cards.info.processing-fee', {
                                    fee: formatPrice(prices.processingFee)
                                })}
                            </p>
                        </div>
                    )}

                    {totalItems > 0 && (
                        <div
                            className="p-4 rounded-8 text-center"
                            style={{
                                backgroundColor: withOpacity(domainConfiguration.style.primaryColor, 0.1),
                                borderColor: domainConfiguration.style.primaryColor
                            }}>
                            <p
                                className="text-body-16 font-semibold"
                                style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                {t('buy-gift-cards.form.total-selected', { count: totalItems })}
                            </p>
                        </div>
                    )}
                </div>

                {/* Separator */}
                <div
                    className="w-full h-1"
                    style={{
                        backgroundColor: withOpacity(domainConfiguration.style.onSurfaceColor, 0.3)
                    }}
                />

                {/* Order Summary */}
                {totalItems > 0 && (
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span
                                className="text-body-16"
                                style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                {t('buy-gift-cards.form.subtotal')}
                            </span>
                            <span
                                className="text-body-16 font-semibold"
                                style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                {(() => {
                                    const subtotal =
                                        (watchedValues[0] || 0) * (prices?.giftVoucher || 0) +
                                        (watchedValues[1] || 0) * (prices?.concessionVoucher || 0) +
                                        (watchedValues[2] || 0) * (prices?.dboxVoucher || 0) +
                                        (watchedValues[3] || 0) *
                                            ((prices?.giftVoucher || 0) * 3 + (prices?.concessionVoucher || 0) * 3) + // Classic Package
                                        (watchedValues[4] || 0) *
                                            ((prices?.dboxVoucher || 0) * 2 +
                                                (prices?.giftVoucher || 0) * 2 +
                                                (prices?.concessionVoucher || 0) * 4) + // Premium Package
                                        (watchedValues[5] || 0) * (prices?.goldenEnvelope || 0);
                                    return formatPrice(subtotal);
                                })()}
                            </span>
                        </div>

                        {prices?.processingFee && (
                            <div className="flex justify-between items-center">
                                <span
                                    className="text-body-14"
                                    style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7) }}>
                                    {t('buy-gift-cards.form.processing-fee')}
                                </span>
                                <span
                                    className="text-body-14"
                                    style={{ color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7) }}>
                                    {formatPrice(prices.processingFee)}
                                </span>
                            </div>
                        )}

                        <div
                            className="w-full h-px"
                            style={{
                                backgroundColor: withOpacity(domainConfiguration.style.onSurfaceColor, 0.2)
                            }}
                        />

                        <div className="flex justify-between items-center">
                            <span
                                className="text-headline-18 font-bold"
                                style={{ color: domainConfiguration.style.onSurfaceColor }}>
                                {t('buy-gift-cards.form.total')}
                            </span>
                            <span
                                className="text-headline-18 font-bold"
                                style={{ color: domainConfiguration.style.primaryColor }}>
                                {(() => {
                                    const subtotal =
                                        (watchedValues[0] || 0) * (prices?.giftVoucher || 0) +
                                        (watchedValues[1] || 0) * (prices?.concessionVoucher || 0) +
                                        (watchedValues[2] || 0) * (prices?.dboxVoucher || 0) +
                                        (watchedValues[3] || 0) *
                                            ((prices?.giftVoucher || 0) * 3 + (prices?.concessionVoucher || 0) * 3) + // Classic Package
                                        (watchedValues[4] || 0) *
                                            ((prices?.dboxVoucher || 0) * 2 +
                                                (prices?.giftVoucher || 0) * 2 +
                                                (prices?.concessionVoucher || 0) * 4) + // Premium Package
                                        (watchedValues[5] || 0) * (prices?.goldenEnvelope || 0);
                                    const total = subtotal + (prices?.processingFee || 0);
                                    return formatPrice(total);
                                })()}
                            </span>
                        </div>
                    </div>
                )}

                {/* Personal Information */}
                <div>
                    <h3
                        className="text-headline-20 font-semibold mb-6"
                        style={{ color: domainConfiguration.style.onSurfaceColor }}>
                        {t('buy-gift-cards.form.personal-info')}
                    </h3>
                    <div className="grid gap-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <FormInput
                                name="firstName"
                                label={t('buy-gift-cards.form.first-name')}
                                type="text"
                                register={register}
                                registerOptions={{ required: true }}
                                required
                                domainConfiguration={domainConfiguration}
                            />
                            <FormInput
                                name="lastName"
                                label={t('buy-gift-cards.form.last-name')}
                                type="text"
                                register={register}
                                registerOptions={{ required: true }}
                                required
                                domainConfiguration={domainConfiguration}
                            />
                        </div>
                        <FormInput
                            name="firm"
                            label={t('buy-gift-cards.form.firm')}
                            type="text"
                            register={register}
                            domainConfiguration={domainConfiguration}
                        />
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="md:col-span-2">
                                <FormInput
                                    name="street"
                                    label={t('buy-gift-cards.form.street')}
                                    type="text"
                                    register={register}
                                    registerOptions={{ required: true }}
                                    required
                                    domainConfiguration={domainConfiguration}
                                />
                            </div>
                            <FormInput
                                name="houseNumber"
                                label={t('buy-gift-cards.form.house-number')}
                                type="text"
                                register={register}
                                registerOptions={{ required: true }}
                                required
                                domainConfiguration={domainConfiguration}
                            />
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            <FormInput
                                name="postalCode"
                                label={t('buy-gift-cards.form.postal-code')}
                                type="text"
                                register={register}
                                registerOptions={{ required: true }}
                                required
                                domainConfiguration={domainConfiguration}
                            />
                            <div className="md:col-span-2">
                                <FormInput
                                    name="city"
                                    label={t('buy-gift-cards.form.city')}
                                    type="text"
                                    register={register}
                                    registerOptions={{ required: true }}
                                    required
                                    domainConfiguration={domainConfiguration}
                                />
                            </div>
                        </div>
                        <FormInput
                            name="email"
                            label={t('buy-gift-cards.form.email')}
                            type="email"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                            domainConfiguration={domainConfiguration}
                        />
                        <FormTextarea
                            name="message"
                            label={t('buy-gift-cards.form.message')}
                            register={register}
                            placeholder={t('buy-gift-cards.form.message-placeholder')}
                            domainConfiguration={domainConfiguration}
                        />
                    </div>
                </div>

                {/* Separator */}
                <div
                    className="w-full h-1"
                    style={{
                        backgroundColor: withOpacity(domainConfiguration.style.onSurfaceColor, 0.3)
                    }}
                />

                {/* Required Fields Note */}
                <div>
                    <p
                        className="text-body-12 text-center"
                        style={{
                            color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7)
                        }}>
                        {t('buy-gift-cards.form.required-fields')}
                    </p>
                </div>

                {/* Submit Button */}
                <div className="pt-8">
                    <TurnstileWidget onVerify={setTurnstileToken} />
                    <Button
                        type="submit"
                        disabled={isProcessing || totalItems === 0}
                        className="w-full py-4 text-body-16 font-semibold transition-all duration-200 rounded-8"
                        css={{
                            backgroundColor: domainConfiguration.style.primaryColor,
                            color: domainConfiguration.style.onPrimaryColor,
                            opacity: isProcessing || totalItems === 0 ? 0.6 : 1
                        }}
                        leadingIcon={isProcessing ? <IconLoading16 className="animate-spin" /> : undefined}>
                        {isProcessing ? t('buy-gift-cards.form.submitting') : t('buy-gift-cards.form.submit')}
                    </Button>
                </div>
            </form>
        </div>
    );
}
